import type { Moved, PublicZone } from "./protocol";

// The table's sound effects, from JDSherbert's Tabletop Games SFX Pack (sounds/NOTICE.md). Each
// plays from its first audible sample, and not again within a beat of itself; until the player's
// first press lets the page make sound, none play.

export type Sound =
  | "click" | "card-hover" | "card-pickup" | "card-drop"
  | "draw" | "cast" | "to-graveyard" | "shuffle" | "dice-roll" | "turn";

const FILES: Record<Sound, string[]> = {
  click: ["piece-impact-1", "piece-impact-2"],
  "card-hover": ["deck-deal-1", "deck-deal-2"],
  "card-pickup": ["paper-flip-1"],
  "card-drop": ["piece-impact-1", "piece-impact-2"],
  draw: ["deck-deal-1", "deck-deal-2"],
  cast: ["piece-move-1", "piece-move-2"],
  "to-graveyard": ["paper-flip-1"],
  shuffle: ["deck-shuffle-1", "deck-shuffle-2"],
  "dice-roll": ["dice-roll-1"],
  turn: ["dice-pickup-1", "dice-pickup-2"],
};

const VOLUME: Record<Sound, number> = {
  click: 0.35, "card-hover": 0.12, "card-pickup": 0.35, "card-drop": 0.45, draw: 0.6, cast: 0.7, "to-graveyard": 0.6, shuffle: 0.6, "dice-roll": 0.6, turn: 0.5,
};

// The table's own sounds play out; a hover's or a pickup's is only its first touch of the card.
const CUT_AFTER_S: Partial<Record<Sound, number>> = { "card-hover": 0.15, "card-pickup": 0.35 };
const FADE_S = 0.04;

const REPEAT_GAP_MS = 80;
const AUDIBLE_LEVEL = 0.01;
const SOUND_EFFECTS_KEY = "spell-mtg.sound-effects";

export function loadSoundEffects(): boolean {
  try { return localStorage.getItem(SOUND_EFFECTS_KEY) !== "off"; } catch { return true; }
}

export function saveSoundEffects(on: boolean): void {
  try { localStorage.setItem(SOUND_EFFECTS_KEY, on ? "on" : "off"); } catch { /* the toggle still holds for this tab */ }
}

type Clip = { buffer: AudioBuffer; startsAt: number };

let audio: AudioContext | null = null;
const decoding = new Set<string>();
const clips = new Map<string, Clip>();
const lastPlayed = new Map<Sound, number>();

function firstAudibleSecond(buffer: AudioBuffer): number {
  const samples = buffer.getChannelData(0);
  const first = samples.findIndex((s) => Math.abs(s) > AUDIBLE_LEVEL);
  return first < 0 ? 0 : first / buffer.sampleRate;
}

// Decoded ahead of the first press, in a context that makes no sound, so that press already has its click.
export function decodeSounds(): void {
  const decoder = new OfflineAudioContext(1, 1, 48000);
  for (const name of Object.values(FILES).flat()) {
    if (decoding.has(name)) continue;
    decoding.add(name);
    void fetch(`sounds/${name}.mp3`)
      .then((r) => r.arrayBuffer())
      .then((bytes) => decoder.decodeAudioData(bytes))
      .then((buffer) => { clips.set(name, { buffer, startsAt: firstAudibleSecond(buffer) }); })
      .catch(() => { /* that clip stays silent */ });
  }
}

// From the first press on: before one, a browser keeps a page's audio suspended.
export function startSoundsOnPress(): void {
  audio ??= new AudioContext();
  if (audio.state === "suspended") void audio.resume();
}

export function playSound(sound: Sound): void {
  if (!audio || !loadSoundEffects()) return;
  const now = performance.now();
  if (now - (lastPlayed.get(sound) ?? -Infinity) < REPEAT_GAP_MS) return;
  const names = FILES[sound];
  const clip = clips.get(names[Math.floor(Math.random() * names.length)]);
  if (!clip) return;
  lastPlayed.set(sound, now);
  const source = audio.createBufferSource();
  const level = audio.createGain();
  source.buffer = clip.buffer;
  level.gain.value = VOLUME[sound];
  source.connect(level).connect(audio.destination);
  source.start(0, clip.startsAt);
  const cut = CUT_AFTER_S[sound];
  if (cut !== undefined) {
    const end = audio.currentTime + cut;
    level.gain.setValueAtTime(VOLUME[sound], end - FADE_S);
    level.gain.linearRampToValueAtTime(0, end);
    source.stop(end);
  }
}

const isZone = (zone: PublicZone, ...kinds: PublicZone["kind"][]): boolean => kinds.includes(zone.kind);

// The one sound an update's moves make, the rarest first: a mulligan's shuffle over its redraw.
export function soundOfMoves(moves: Moved[]): Sound | null {
  if (moves.some((m) => isZone(m.from_zone, "Hand") && isZone(m.to_zone, "Library"))) return "shuffle";
  if (moves.some((m) => isZone(m.from_zone, "Library") && isZone(m.to_zone, "Hand"))) return "draw";
  if (moves.some((m) => isZone(m.to_zone, "Stack", "Battlefield"))) return "cast";
  if (moves.some((m) => isZone(m.to_zone, "Graveyard", "Exile"))) return "to-graveyard";
  return null;
}
